"use client";

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useState, useMemo, useRef } from 'react';
import { Loader2, Check } from 'lucide-react';
import { useAuditorExport } from './use-auditor-export';
import { AuditorExportDialogProps } from './auditor-export.types';
import { cn } from '@/lib/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faXmark, 
    faFileExport,
    faSparkles,
    faCalendarDays,
    faShieldCheck,
    faCircleCheck,
    faCircleExclamation,
    faTriangleExclamation
} from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { DatePicker } from "@/components/ui/date-picker";

export function AuditorExportDialog({
    isOpen,
    onClose,
    companyID = window.companyID,
    onExportComplete,
    onError,
}: AuditorExportDialogProps) {
    const {
        isLoading,
        isExporting,
        error,
        frameworks,
        dateRange,
        progress,
        toggleFramework,
        handleDateChange,
        exportAuditPack,
        isSuccess,
    } = useAuditorExport(companyID);

    const [isProcessing, setIsProcessing] = useState(false);

    // Parse date strings to Date objects for DatePicker
    const startDate = useMemo(() => dateRange.startDate ? new Date(dateRange.startDate) : null, [dateRange.startDate]);
    const endDate = useMemo(() => dateRange.endDate ? new Date(dateRange.endDate) : null, [dateRange.endDate]);

    const handleExport = async () => {
        setIsProcessing(true);
        try {
            const result = await exportAuditPack();
            if (result && onExportComplete) {
                onExportComplete(result);
            } else if (!result && onError && error) {
                onError(error);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        if (!isProcessing && !isExporting) {
            onClose();
        }
    };

    const selectedCount = frameworks.filter(f => f.checked).length;
    const canExport = selectedCount > 0 && !isExporting && !isProcessing;
    
    // Track last clicked index for shift-click range selection
    const lastClickedIndex = useRef<number | null>(null);
    
    const handleFrameworkClick = (index: number, event: React.MouseEvent) => {
        const framework = frameworks[index];
        
        if (event.shiftKey && lastClickedIndex.current !== null) {
            // Shift-click: select range from last clicked to current
            const start = Math.min(lastClickedIndex.current, index);
            const end = Math.max(lastClickedIndex.current, index);
            
            // Select all frameworks in range
            for (let i = start; i <= end; i++) {
                if (!frameworks[i].checked) {
                    toggleFramework(frameworks[i].id);
                }
            }
        } else {
            // Normal click: toggle single framework
            toggleFramework(framework.id);
        }
        
        lastClickedIndex.current = index;
    };

    // Processing/Exporting overlay - CHECK FIRST so it takes priority over loading state
    if (isProcessing || isExporting) {
        const progressPercent = progress ? Math.round((progress.current / Math.max(progress.total, 1)) * 100) : 0;
        const stageLabels: Record<string, string> = {
            preparing: 'Preparing',
            controls: 'Processing Controls',
            clauses: 'Processing Clauses',
            policies: 'Processing Policies',
            tasks: 'Processing Tasks',
            docs: 'Processing Documents',
            generating: 'Generating Workbook',
            complete: 'Complete'
        };
        const currentStageLabel = progress?.stage ? stageLabels[progress.stage] || progress.stage : 'Processing';
        
        return (
            <Dialog open={isOpen} onOpenChange={() => {}}>
                <DialogContent 
                    className="max-w-3xl! w-3xl! bg-transparent shadow-none border-0 p-0 [&>button]:hidden"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
                        <div className="p-8 text-center min-h-[300px] flex flex-col items-center justify-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-primary/10 to-primary/30 mb-4">
                                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                            </div>
                            <h2 className="text-2xl font-bold text-foreground">Exporting Auditor Pack</h2>
                            
                            {/* Progress section */}
                            <div className="mt-6 w-full max-w-md">
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-muted-foreground">{currentStageLabel}</span>
                                    <span className="text-primary font-medium">{progressPercent}%</span>
                                </div>
                                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-primary transition-all duration-300 ease-out rounded-full"
                                        style={{ width: `${progressPercent}%` }}
                                    />
                                </div>
                                <p className="mt-3 text-sm text-muted-foreground">
                                    {progress?.message || 'Please wait while we generate your auditor pack...'}
                                </p>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // Loading state (initial framework load only)
    if (isLoading) {
        return (
            <Dialog open={isOpen} onOpenChange={handleClose}>
                <DialogContent 
                    className="max-w-3xl! w-3xl! bg-transparent shadow-none border-0 p-0 [&>button]:hidden"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
                        {/* Custom close button */}
                        <button
                            onClick={handleClose}
                            className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                        >
                            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-foreground/70" />
                        </button>
                        
                        <div className="p-8 flex items-center justify-center min-h-[300px]">
                            <div className="flex flex-col items-center gap-4">
                                <div className="w-16 h-16 rounded-full bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                </div>
                                <p className="text-muted-foreground font-medium">Loading frameworks...</p>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // Success state
    if (isSuccess) {
        return (
            <Dialog open={isOpen} onOpenChange={handleClose}>
                <DialogContent 
                    className="max-w-3xl! w-3xl! bg-transparent shadow-none border-0 p-0 [&>button]:hidden"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
                        <button
                            onClick={handleClose}
                            className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                            aria-label="Close"
                        >
                            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
                        </button>
                        
                        <div className="p-8 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-emerald-500/10 to-emerald-500/30 mb-4">
                                <FontAwesomeIcon icon={faCircleCheck} className="w-8 h-8 text-emerald-500" />
                            </div>
                            <h2 className="text-2xl font-bold text-foreground">Export Successful</h2>
                            <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                                Your auditor pack has been exported successfully. The download should start automatically.
                            </p>
                            <Button onClick={handleClose} className="mt-6">
                                Close
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // Error state (only show if error and not processing)
    if (error && !isProcessing) {
        return (
            <Dialog open={isOpen} onOpenChange={handleClose}>
                <DialogContent 
                    className="max-w-3xl! w-3xl! bg-transparent shadow-none border-0 p-0 [&>button]:hidden"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
                        <button
                            onClick={handleClose}
                            className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                            aria-label="Close"
                        >
                            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
                        </button>
                        
                        <div className="p-8 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-red-500/10 to-red-500/30 mb-4">
                                <FontAwesomeIcon icon={faCircleExclamation} className="w-8 h-8 text-red-500" />
                            </div>
                            <h2 className="text-2xl font-bold text-foreground">Export Failed</h2>
                            <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                                {error.message}
                            </p>
                            <Button onClick={handleClose} variant="outline" className="mt-6">
                                Close
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // Main dialog
    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent 
                className="max-w-3xl! w-3xl! h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center [&>button]:hidden"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
                    {/* Close button */}
                    <button
                        onClick={handleClose}
                        className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
                    </button>
                    
                    {/* Header */}
                    <div className="p-6 pb-4 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent shrink-0">
                        <h2 className="text-3xl font-bold text-foreground">Export Auditor Pack</h2>
                        <p className="mt-2 text-muted-foreground">
                            Select the compliance frameworks to include in your auditor pack export.
                        </p>
                    </div>

                    {/* Form Content - scrollable */}
                    <div className="p-6 flex-1 overflow-y-auto space-y-6">
                        {/* Frameworks Selection Header */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="shrink-0 w-10 h-10 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faShieldCheck} className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <Label className="block text-sm font-semibold text-foreground">
                                        Compliance Frameworks <span className="text-destructive">*</span>
                                    </Label>
                                    <p className="text-xs text-muted-foreground">
                                        Select which frameworks to include
                                    </p>
                                </div>
                            </div>
                            {selectedCount > 0 && (
                                <span className="text-xs text-primary bg-primary/10 px-3 py-1.5 rounded-full font-medium">
                                    {selectedCount} selected
                                </span>
                            )}
                        </div>
                        
                        {/* Framework Cards Grid */}
                        {frameworks.length === 0 ? (
                            <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-4 flex items-start gap-3">
                                <FontAwesomeIcon icon={faTriangleExclamation} className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-medium text-amber-600 dark:text-amber-400">No enabled modules found</p>
                                    <p className="mt-1 text-sm text-amber-600/80 dark:text-amber-400/80">
                                        Please enable compliance modules in your organisation settings to export an auditor pack.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {frameworks.map((framework, index) => {
                                    const isSelected = framework.checked;
                                    return (
                                        <div
                                            key={framework.id}
                                            onClick={(e) => handleFrameworkClick(index, e)}
                                            style={{
                                                background: isSelected ? 'linear-gradient(135deg, rgba(var(--primary-rgb), 0.15), rgba(var(--primary-rgb), 0.05))' : '',
                                                backdropFilter: 'blur(8px)'
                                            }}
                                            className={cn(
                                                "relative overflow-hidden rounded-xl p-4 cursor-pointer transition-all duration-300 transform hover:scale-[1.02]",
                                                isSelected
                                                    ? "border-2 border-primary shadow-lg"
                                                    : "bg-white/5 hover:bg-white/10 dark:bg-gray-800/30 dark:hover:bg-gray-800/50 border border-white/10 shadow-sm hover:shadow-md"
                                            )}
                                        >
                                            {/* Light accent when selected */}
                                            {isSelected && (
                                                <div className="absolute inset-0 bg-linear-to-tr from-primary/5 via-transparent to-primary/10 pointer-events-none" />
                                            )}
                                            
                                            {/* Selection indicator */}
                                            {isSelected && (
                                                <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary flex items-center justify-center shadow-sm z-20">
                                                    <Check className="h-3 w-3 text-primary-foreground" />
                                                </div>
                                            )}
                                            
                                            <div className="relative z-10">
                                                <div className="text-foreground font-medium">{framework.label}</div>
                                                <div className="text-xs text-muted-foreground mt-1">Compliance Framework</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Date Range */}
                        <div className="rounded-xl border border-primary/20 bg-linear-to-br from-primary/5 to-primary/15 backdrop-blur-sm p-5 space-y-4 pb-10">
                            <div className="flex items-start gap-3">
                                <div className="shrink-0 w-10 h-10 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faCalendarDays} className="w-5 h-5 text-primary" />
                                </div>
                                <div className="flex-1">
                                    <Label className="block text-sm font-semibold text-foreground">
                                        Assurance Tasks Date Range
                                    </Label>
                                    <p className="text-xs text-muted-foreground">
                                        Filter assurance tasks to include only those within this date range
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                        Start Date
                                    </Label>
                                    <DatePicker
                                        date={startDate ?? undefined}
                                        setDate={(date) => handleDateChange('startDate', date ? date.toISOString().split('T')[0] : '')}
                                        className="bg-white/5 border-primary/10 h-11 rounded-lg w-full"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                        End Date
                                    </Label>
                                    <DatePicker
                                        date={endDate ?? undefined}
                                        setDate={(date) => handleDateChange('endDate', date ? date.toISOString().split('T')[0] : '')}
                                        className="bg-white/5 border-primary/10 h-11 rounded-lg w-full"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Info Note */}
                        <div className="rounded-lg bg-primary/5 border border-primary/10 p-4 flex items-start gap-3">
                            <FontAwesomeIcon icon={faSparkles} className="w-5 h-5 text-cyan-500 shrink-0 mt-0.5" />
                            <p className="text-sm text-muted-foreground">
                                <span className="font-medium text-foreground">Tip:</span> The auditor pack includes your Statement of Applicability, policies, procedures, and completed assurance tasks for the selected frameworks.
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t border-white/10 px-6 py-4 bg-linear-to-t from-primary/5 to-transparent flex justify-between items-center shrink-0">
                        <p className="text-xs text-muted-foreground">
                            {frameworks.length === 0 
                                ? "No frameworks available" 
                                : selectedCount === 0
                                    ? "Select at least one framework to continue"
                                    : `Ready to export ${selectedCount} framework${selectedCount > 1 ? 's' : ''}`}
                        </p>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button 
                                disabled={!canExport} 
                                onClick={handleExport} 
                                className="min-w-[140px]"
                            >
                                <FontAwesomeIcon icon={faFileExport} className="mr-2 h-4 w-4" />
                                Export Pack
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
